import styled from "styled-components/macro";
import { TechSector } from "../../config/tech-sector";
import { ReactComponent as IconWater } from '../../assets/icon-water.svg';
import { ReactComponent as IconWasteWater } from '../../assets/icon-waste-water.svg';
import { ReactComponent as IconHeating } from '../../assets/icon-heating.svg';
import { ReactComponent as IconFireProtection } from '../../assets/icon-fire-protection.svg';
import { getTechSectorBackground } from "../utils/colors";
import { forwardRef } from "react";
import NewFlag from "./newflag";
import useTranslations from "../utils/language-provider";
import { isMobileDevice } from "../utils/mobile-detection";


type TechSectorIconProps = {
    sectorType: TechSector;
    inactive?: boolean;
    style?: React.CSSProperties;
    className?: string;
    onHover?: [() => void, () => void];
    newSector?: boolean;
    sectorPosition?: 'top' | 'bottom';
    buildingNav?: boolean;
    isBigFlag?: boolean;
}

const IconMapping = {
    [TechSector.FIRE_PROTECTION]: <IconFireProtection />,
    [TechSector.WASTE_WATER]: <IconWasteWater />,
    [TechSector.WATER]: <IconWater />,
    [TechSector.HEATING]: <IconHeating />,
}

const TechSectorBackground = styled.div<TechSectorIconProps>`
    position: absolute;
    z-index: -1;
    bottom: 0;
    background: ${({ theme, sectorType, inactive }) => getTechSectorBackground(theme, sectorType, inactive)};
    width: 100%;
    height: ${(props) => (props.buildingNav ? (isMobileDevice() ? '5px' : '15px') : '100%')};
    transition: width 300ms;
`;

const IconWrapper = styled.div<TechSectorIconProps>`
    position: relative;
     ${(props) =>
         props.isBigFlag
             ? `
            display: flex;
            justify-content: center;
            align-items: center;`
             : `
            display: grid;

            ${
                props.buildingNav
                    ? `
                padding: 0 10px;
                grid-template-columns: 20px 1fr;

                @media ${props.theme.screens.lg} {
                    grid-template-columns: 30px 1fr;
                    padding: 0;
                }
            `
                    : `
                padding-left: 20px;
                grid-template-columns: 30px 1fr;
            `
            }
    `};
    ${(props) => (props.isBigFlag ? '' : 'justify-content: space-around;')}
    transition: all 300ms;
    transform-origin: center;

    svg {
        position: relative;
        flex: 0 0 auto;
        bottom: ${(props) => (props.sectorPosition !== 'top' ? (isMobileDevice() ? '0' : '-5px') : '5px')};
        ${(props) =>
            props.buildingNav
                ? `
                width: 20px;
                height: 20px;
        `
                : `
                width: 25px;
                height: 25px;
        `}
    }


    @media ${(props) => props.theme.screens.lg} {
        &:not(.active):hover {
            ${TechSectorBackground} {
                ${(props) => (props.inactive ? '' : props.buildingNav ? 'height: 115px;' : 'height: 150%;')}
                ${(props) => (props.sectorPosition !== 'top' && !props.buildingNav ? 'bottom: -8px;' : '')}
                transition: height 300ms, bottom 300ms;
            }
        }
        display: flex;
        flex-direction: column;
        padding-left: 0;
        svg{
            ${(props) =>
                props.isBigFlag
                    ? `
                width: 25px;
                height: 25px;
        `
                    : props.buildingNav
                    ? `
                width: 100%;
                height: 45px;
        `
                    : `
                width: 100%;
                max-height: 15px;
                aspect-ratio: 1/1;
        `}
        }
    }
`;

const Title = styled.h3`
    margin: 0;
    text-align: center;
    font-size: ${props => props.theme.typography.xs};
    color: ${props => props.theme.colors.white};

    @media ${props => props.theme.screens.lg} {
        margin-bottom: 20px;
        font-size: ${props => props.theme.typography.lg};
    }
`

const TechSectorIcon = forwardRef<HTMLDivElement, TechSectorIconProps>(({
    sectorType,
    onHover = [() => {}, () => {}],
    className,
    ...props
}, ref) => {
    const translations = useTranslations();
    return (
        <IconWrapper
            ref={ref}
            className={className}
            sectorType={sectorType}
            onMouseEnter={() => onHover[0]()}
            onMouseLeave={() => onHover[1]()}
            {...props}
            isBigFlag={props.isBigFlag}
        >
            {IconMapping[sectorType]}
            {props.newSector && <NewFlag type='tech-sector-city' >{translations.buildingDetails.new}</NewFlag>}
            {props.buildingNav && <Title>{translations.sectors[sectorType].name}</Title>}
            <TechSectorBackground sectorType={sectorType} inactive={props.inactive} buildingNav={props.buildingNav} isBigFlag={props.isBigFlag} />
        </IconWrapper>
    );
})

export default TechSectorIcon;
